// frontend/src/components/common/AppToast.jsx
//
// The app's one toast, mounted once in App.jsx above every tab. Two signals
// reach it:
//   - ./appToastBus.js — showToast, called from every page, dialog and hook.
//   - tabs/capEvent.js — the tab cap, emitted by openers that cannot open one
//     more tab. The copy is composed here because the emitter has no i18n.
// One toast at a time; a newer one replaces what is showing.
//
// Bottom-center of the window, the spot the Shopify admin's own toast uses,
// so it never sits over a popup's fields; filled, so it reads over any page.
// Popups center on the window too (MUI's default), and the toast's spot never
// depends on the sidebar being visible.

import { useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AlertActionSlot from './AlertActionSlot';
import { APP_TOAST_EVENT } from './appToastBus';
import { CAP_EVENT } from '../../tabs/capEvent';
import { MAX_TABS } from '../../tabs/tabsContexts';

const TOAST_HOLD_MS = 5000;

export default function AppToast() {
  const { t } = useTranslation();
  // `open` and the content are separate: the slide-out plays after close, so
  // the words and colour must outlive `open` until the exit has finished.
  const [toast, setToast] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = (next) => { setToast(next); setOpen(true); };
    const onToast = (e) => show(e.detail);
    const onCap = () => show({ message: t('tabs.capReached', { max: MAX_TABS }), severity: 'warning' });
    window.addEventListener(APP_TOAST_EVENT, onToast);
    window.addEventListener(CAP_EVENT, onCap);
    return () => {
      window.removeEventListener(APP_TOAST_EVENT, onToast);
      window.removeEventListener(CAP_EVENT, onCap);
    };
  }, [t]);

  const close = () => setOpen(false);

  if (!toast) return null;
  return (
    <Snackbar
      open={open}
      autoHideDuration={TOAST_HOLD_MS}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionProps={{ onExited: () => setToast(null) }}
    >
      <Alert
        severity={toast.severity}
        variant="filled"
        action={(
          <AlertActionSlot onDismiss={close}>
            {toast.action && (
              <Button color="inherit" onClick={() => { toast.action.onClick(); close(); }}>
                {toast.action.label}
              </Button>
            )}
          </AlertActionSlot>
        )}
      >
        {toast.message}
      </Alert>
    </Snackbar>
  );
}
